import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box } from '@mui/material';
import { theme } from './theme/theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SiteProvider } from './contexts/SiteContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout/Layout';

// Lazy load page components for code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'));
const CustomersListPage = lazy(() => import('./pages/CustomersListPage'));
const CustomerSitesPage = lazy(() => import('./pages/CustomerSitesPage'));
const SitesListPage = lazy(() => import('./pages/SitesListPage'));
const ScanPage = lazy(() => import('./pages/ScanPage'));
const ScanResultsPage = lazy(() => import('./pages/ScanResultsPage'));
const ServerErrorPage = lazy(() => import('./pages/ServerErrorPage'));
const DistributedAgentsPage = lazy(() => import('./pages/DistributedAgentsPage'));
const SitePage = lazy(() => import('./pages/SitePage'));

// Loading fallback component
const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <CircularProgress size={60} />
  </Box>
);

// Component to handle root route redirection
const RootRedirect: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return null; // Let ProtectedRoute handle loading state
  }
  
  return <Navigate to={isAuthenticated ? "/customers" : "/login"} replace />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <SiteProvider>
          <Router>
            <div className="App">
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                <Route path="/" element={<RootRedirect />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/server-error" element={<ServerErrorPage />} />
              
              {/* Distributed Agents route */}
              <Route 
                path="/distributed-agents" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DistributedAgentsPage />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Customer hierarchy routes */}
              <Route 
                path="/customers" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CustomersListPage />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/customers/:customerId/sites" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CustomerSitesPage />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/customers/:customerId/sites/:siteId" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SitePage />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Customer hierarchy scan and history routes */}
              <Route 
                path="/customers/:customerId/sites/:siteId/scan" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ScanPage />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/customers/:customerId/sites/:siteId/scans/:scanId" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ScanResultsPage />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Legacy sites route - keep for backward compatibility */}
              <Route 
                path="/sites" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SitesListPage />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/sites/:siteId/scan" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ScanPage />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/sites/:siteId/scans/:scanId" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ScanResultsPage />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              {/* Legacy routes - redirect to sites */}
              <Route path="/scan" element={<Navigate to="/sites" replace />} />
              <Route path="/history" element={<Navigate to="/sites" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </div>
          </Router>
        </SiteProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
